import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { DownOutlined, CheckOutlined } from '@ant-design/icons';
import './SellMegaMenu.css';

const SELL_DEST = '/dashboard/add-user-listing';

const VEHICLE_TYPES = [
  { key: 'vehicle_car',        img: '/images/categories/Sedan.png' },
  { key: 'vehicle_suv',        img: '/images/categories/SUV.png' },
  { key: 'vehicle_pickup',     img: '/images/categories/Pickup.png' },
  { key: 'vehicle_van',        img: '/images/categories/VAN.png' },
  { key: 'vehicle_camper_van', img: '/images/categories/CamperVan.png' },
  { key: 'vehicle_coupe',      img: '/images/categories/Coup.png' },
  { key: 'vehicle_hatchback',  img: '/images/categories/Hatchback.png' },
  { key: 'vehicle_cabriolet',  img: '/images/categories/Cabriolet.png' },
  { key: 'vehicle_minivan',    img: '/images/categories/Minivan.png' },
  { key: 'vehicle_supercar',   img: '/images/categories/Supercar.png' },
];

const WHY_POINT_KEYS = [
  'why_free_listing',
  'why_verified_buyers',
  'why_featured',
  'why_secure',
];

const DesktopPanel: React.FC<{ onNavigate: (href: string) => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="smm-panel">
      <div className="smm-inner">
        {/* Left – vehicle type grid */}
        <div>
          <div className="smm-col-title">{t('nav.sell_menu.what_are_you_selling')}</div>
          <div className="smm-vehicle-grid">
            {VEHICLE_TYPES.map(v => {
              const label = t(`nav.sell_menu.${v.key}`);
              return (
                <a
                  key={v.key}
                  className="smm-vehicle-item"
                  href="#"
                  onClick={e => { e.preventDefault(); onNavigate(SELL_DEST); }}
                >
                  <div className="smm-vehicle-img-wrap">
                    <img className="smm-vehicle-img" src={v.img} alt={label} />
                  </div>
                  <span className="smm-vehicle-label">{label}</span>
                </a>
              );
            })}
          </div>
        </div>

        {/* Right – why sell */}
        <div className="smm-why">
          <div className="smm-why-title">{t('nav.sell_menu.why_title')}</div>
          <ul className="smm-why-list">
            {WHY_POINT_KEYS.map(k => (
              <li key={k} className="smm-why-item">
                <span className="smm-why-icon"><CheckOutlined /></span>
                {t(`nav.sell_menu.${k}`)}
              </li>
            ))}
          </ul>
          <a className="smm-cta" href="#" onClick={e => { e.preventDefault(); onNavigate(SELL_DEST); }}>
            {t('nav.sell_menu.post_listing_cta')}
          </a>
        </div>
      </div>

      <div className="smm-footer">
        <div className="smm-footer-inner">
          <Trans i18nKey="nav.sell_menu.footer_tagline" components={[<strong />, <strong />]} />
        </div>
      </div>
    </div>
  );
};

interface SellMegaMenuProps {
  mobile?: boolean;
}

const SellMegaMenu: React.FC<SellMegaMenuProps> = ({ mobile = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavigate = (href: string) => {
    setOpen(false);
    navigate(href);
  };

  if (mobile) {
    return (
      <li className="mob-nav-item">
        <button className="mob-nav-trigger" onClick={() => setOpen(p => !p)}>
          {t('nav.sell')}
          <DownOutlined className={`mob-nav-chevron ${open ? 'mob-nav-chevron-open' : ''}`} />
        </button>
        {open && (
          <div className="mob-nav-sub">
            {VEHICLE_TYPES.map(v => (
              <a key={v.key} className="mob-nav-sub-link" href="#" onClick={e => { e.preventDefault(); handleNavigate(SELL_DEST); }}>
                {t(`nav.sell_menu.${v.key}`)}
              </a>
            ))}
          </div>
        )}
      </li>
    );
  }

  return (
    <li ref={containerRef} className="nav-item smm-nav-item">
      <a
        className="nav-link smm-trigger"
        href="#"
        onClick={e => { e.preventDefault(); setOpen(p => !p); }}
      >
        {t('nav.sell')}
        <DownOutlined className={`smm-chevron ${open ? 'smm-chevron-open' : ''}`} />
      </a>
      {open && <DesktopPanel onNavigate={handleNavigate} />}
    </li>
  );
};

export default SellMegaMenu;
